import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";

export type LeadRevokeCause =
  | "cf_access_lost"
  | "replaced_same_role"
  | "manual";

export interface LeadRevokeSummaryEntry {
  userId: number;
  userName: string;
  userEmail: string | null;
  roleName: string | null;
  level: "lead" | "child";
  childId: number | null;
  childName: string | null;
  revokedAt: string | null;
  revokedByName: string | null;
  cause: LeadRevokeCause | null;
  message: string | null;
}

export interface LeadRevokeSummaryResult {
  leadId: number;
  totalRevoked: number;
  entries: LeadRevokeSummaryEntry[];
}

/**
 * Admin-only "who lost access to this lead?" data. Used by the lead
 * detail drawer under Revoked → No Access so a tenant admin can see
 * concretely who was revoked, when, by whom, and why — instead of the
 * unhelpful "Tenant admin sees everything" line.
 */
export function useLeadRevokeSummary(
  leadId: number | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!leadId;
  return useQuery<LeadRevokeSummaryResult>({
    queryKey: ["lead-revoke-summary", leadId],
    enabled,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/core/leads/${leadId}/revoke-summary`,
      );
      return res.data as LeadRevokeSummaryResult;
    },
    // Drives an informational "revoked access" banner in the lead drawer —
    // not an action gate. Refetches on remount; 30s meant a re-hit on every
    // drawer reopen. RBAC/assignment changes refresh it on the next reopen.
    staleTime: 3 * 60 * 1000,
  });
}
